import {useMutation} from '@tanstack/react-query'
import {MUTATION_KEYS} from './keys'
import {sendChatFile, SendFilePayload} from '../services/chat.http'
import {CreateBroadcastPayload, MessageTypeE, SendBroadcastMessageSchema} from '../chat.model'
import {useParams} from 'react-router-dom'
import {useCreateBroadcastV3} from './useCreateBroadcastV3'
import {useSendBroadcastMessageV3} from './useSendBroadcastMessageV3'
import axios from 'axios'
import {useMe} from '@/features/authentication/queries/useMe'
import {UserRoleE} from '@/types'
import {LocalStorageManager} from '@/utilities/localStorage'

export const useSendBroadcastFileV3 = (
    sourceData?: {
        usersIds: number[]
        goalsIds: number[]
    },
    onSuccess?: () => void
) => {
    const {data: user} = useMe()
    const isRepresentative = user?.type === UserRoleE.Representative
    const representedCreatorId = isRepresentative ? LocalStorageManager.chatUserId.get() : undefined

    const {channel} = useParams()
    const isCreatedBroadcast = channel?.startsWith('group-')
    const broadcastId = isCreatedBroadcast ? channel?.replace('group-', '') : null

    const {mutateAsync: createBroadcast} = useCreateBroadcastV3({})
    const {mutateAsync: sendBroadcastMessage} = useSendBroadcastMessageV3()

    return useMutation({
        mutationKey: [MUTATION_KEYS.sendBroadcastFileV3],
        mutationFn: async ({
            payload,
            file,
            metadata = {}
        }: {
            payload: SendFilePayload
            file: File
            metadata?: {
                morgiAmount?: number
            }
        }) => {
            const resPreLink = await sendChatFile({
                ...payload,
                ...(representedCreatorId && {representedCreatorId})
            })

            if (resPreLink.url) {
                const formData = new FormData()
                Object.entries(resPreLink.fields).forEach(([key, value]) => {
                    formData.append(key, value as string)
                })
                formData.append('file', file)
                await axios.post(resPreLink.url, formData)

                if (isCreatedBroadcast) {
                    const data: SendBroadcastMessageSchema = {
                        messageType: 'media',
                        attachment: {
                            fileUploadId: resPreLink.fileUploadId
                        },
                        message: {
                            type: payload.fileType as unknown as MessageTypeE,
                            metadata
                        }
                    }

                    await sendBroadcastMessage({
                        broadcastGroupId: broadcastId!,
                        payload: data
                    })
                } else {
                    if (!sourceData) return

                    const data: CreateBroadcastPayload = {
                        ...sourceData,
                        messageType: 'media',
                        attachment: {
                            fileUploadId: resPreLink.fileUploadId
                        },
                        message: {
                            type: payload.fileType as unknown as MessageTypeE,
                            metadata
                        }
                    }

                    await createBroadcast(data)
                }
            }
        },
        onSettled: () => {
            onSuccess?.()
        }
    })
}
